<template>
  <div>
    <!-- Десктопная версия -->
    <el-select
      v-model="modelValue"
      class="multi-select !h-auto max-md:!hidden"
      :class="{ 'unset-border': !isBorder }"
      popper-class="multi-select-popper"
      loading-text="Загрузка..."
      no-match-text="Ничего не найдено"
      no-data-text="Нет данных"
      :filterable="filterable"
      :placeholder="placeholder"
      :multiple="multiple"
      :clearable="clearable"
      @update:model-value="$emit('update:modelValue', $event)"
      @visible-change="$emit('visible-change', $event)"
    >
      <template #label="{ label, value }">
        <div class="flex items-center gap-2">
          <span class="leading-4">
            {{ label }}
          </span>
        </div>
      </template>
      <el-option
        v-for="item in options"
        :key="item.value"
        :label="item.label"
        :value="item.value"
        class="flex items-center gap-2"
      >
        <span>
          {{ item.label }}
        </span>
      </el-option>
    </el-select>

    <!-- Мобильная версия -->
    <div 
      @click="openDialog" 
      class="cursor-pointer border rounded-2xl p-3 bg-white md:hidden border-border min-h-[44px] flex items-center"
    >
      <!-- Теги для множественного выбора -->
      <div v-if="multiple && selectedTags.length > 0" class="flex flex-wrap gap-1">
        <span
          v-for="tag in selectedTags"
          :key="tag.value"
          class="inline-flex items-center px-2 py-1 bg-primary text-white text-xs rounded-md"
        >
          {{ tag.label }}
          <button
            @click.stop="removeTag(tag.value)"
            class="ml-1 text-white hover:text-gray-200"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M9 3L3 9M3 3L9 9"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </span>
      </div>
      <!-- Placeholder или текст для одиночного выбора -->
      <div 
        v-else
        class="text-sm font-normal" 
        :class="selectedLabel ? 'text-txmain' : 'text-gray-400'"
      >
        {{ selectedLabel || placeholder }}
      </div>
    </div>

    <!-- Модальное окно для мобильной версии -->
    <Teleport to="body">
      <ElDialog
        v-model="dialogVisible"
        class="select-dialog flex justify-center md:max-w-[496px] !p-0"
        body-class="w-full"
        :show-close="false"
      >
        <div class="flex flex-col w-full h-[calc(100dvh-100px)]">
          <!-- Шапка с кнопкой закрытия -->
          <div class="flex items-center justify-end mb-2">
            <button @click="closeDialog" class="text-txsecondary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M17.7208 6.27916C17.542 6.10042 17.2996 6 17.0467 6C16.7939 6 16.5514 6.10042 16.3726 6.27916L12 10.6518L7.62737 6.27916C7.44857 6.10042 7.2061 6 6.95327 6C6.70044 6 6.45797 6.10042 6.27916 6.27916C6.10042 6.45797 6 6.70044 6 6.95327C6 7.2061 6.10042 7.44857 6.27916 7.62737L10.6518 12L6.27916 16.3726C6.10042 16.5514 6 16.7939 6 17.0467C6 17.2996 6.10042 17.542 6.27916 17.7208C6.45797 17.8996 6.70044 18 6.95327 18C7.2061 18 7.44857 17.8996 7.62737 17.7208L12 13.3482L16.3726 17.7208C16.5514 17.8996 16.7939 18 17.0467 18C17.2996 18 17.542 17.8996 17.7208 17.7208C17.8996 17.542 18 17.2996 18 17.0467C18 16.7939 17.8996 16.5514 17.7208 16.3726L13.3482 12L17.7208 7.62737C17.8996 7.44857 18 7.2061 18 6.95327C18 6.70044 17.8996 6.45797 17.7208 6.27916Z"
                  fill="#1B1C1E"
                  fill-opacity="0.4"
                />
              </svg>
            </button>
          </div>

          <!-- Поле поиска -->
          <div v-if="filterable" class="mb-2">
            <input
              :value="searchQuery"
              @input="onSearchInput"
              @keyup="onSearchInput"
              @compositionstart="onCompositionStart"
              @compositionend="onCompositionEnd"
              type="text"
              placeholder="Поиск..."
              inputmode="search"
              class="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors"
            />
          </div>

          <!-- Список элементов -->
          <div class="flex-1 overflow-y-auto">
            <div v-if="filteredOptions.length === 0" class="text-center py-8 text-txsecondary">
              Ничего не найдено
            </div>
            <div v-else class="space-y-1">
              <div
                v-for="option in filteredOptions"
                :key="option.value"
                @click="selectOption(option)"
                class="px-2 py-2 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
                :class="{ 'bg-primary/10': isSelected(option.value) }"
              >
                <span class="text-sm">{{ option.label }}</span>
                <div class="flex items-center gap-2">
                  <svg
                    v-if="multiple && isSelected(option.value)"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M16.6668 5L7.50016 14.1667L3.3335 10"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="text-primary"
                    />
                  </svg>
                  <svg
                    v-else-if="!multiple && isSelected(option.value)"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M16.6668 5L7.50016 14.1667L3.3335 10"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="text-primary"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Кнопка Применить -->
          <div class="mt-4 pt-4 border-t border-gray-200">
            <button
              @click="props.deferredUpdate ? applyChanges() : closeDialog()"
              class="w-full bg-primary text-white py-3 px-4 rounded-xl font-medium transition-colors hover:bg-primary/90 active:bg-primary/80"
            >
              Применить
            </button>
          </div>
        </div>
      </ElDialog>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
interface Option {
  label: string;
  value: string | number;
}

interface Props {
  options: Option[];
  placeholder?: string;
  multiple?: boolean;
  clearable?: boolean;
  filterable?: boolean;
  isBorder?: boolean;
  deferredUpdate?: boolean; // Обновлять только по кнопке "Применить"
}

const props = defineProps<Props>();
const emit = defineEmits(["update:modelValue", "visible-change"]);

const modelValue = defineModel<any>();

// Состояние для мобильной версии
const dialogVisible = ref(false);
const searchQuery = ref('');
const isComposing = ref(false);

// Временное значение для отложенного обновления
const tempValue = ref<any>(null);

// Инициализация tempValue при монтировании компонента
onMounted(() => {
  if (props.deferredUpdate && tempValue.value === null) {
    tempValue.value = Array.isArray(modelValue.value) ? [...(modelValue.value || [])] : (modelValue.value || null);
  }
});

// Открытие/закрытие диалога
const openDialog = () => {
  dialogVisible.value = true;
  searchQuery.value = '';
  
  // Инициализируем временное значение текущим значением
  if (props.deferredUpdate) {
    tempValue.value = Array.isArray(modelValue.value) ? [...modelValue.value] : modelValue.value;
  }
};

const closeDialog = () => {
  dialogVisible.value = false;
  searchQuery.value = '';
};

const applyChanges = () => {
  if (props.deferredUpdate && tempValue.value !== null) {
    modelValue.value = tempValue.value;
    emit('update:modelValue', tempValue.value);
  }
  closeDialog();
};

// Метод для очистки выбора (экспортируется для внешнего использования)
const clearSelection = () => {
  const emptyValue = props.multiple ? [] : null;
  modelValue.value = emptyValue;
  tempValue.value = emptyValue;
  emit('update:modelValue', emptyValue);
};

// Экспортируем метод для использования через ref
defineExpose({
  clearSelection
});

// Обработка композиции (для IME клавиатур)
const onCompositionStart = () => {
  isComposing.value = true;
};

const onCompositionEnd = (event: CompositionEvent) => {
  isComposing.value = false;
  const target = event.target as HTMLInputElement;
  const newValue = target.value;
  // Принудительное обновление после завершения композиции
  searchQuery.value = newValue;
  nextTick(() => {
    if (searchQuery.value !== newValue) {
      searchQuery.value = newValue;
    }
  });
};

const onSearchInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newValue = target.value;
  // Всегда обновляем значение для совместимости с Яндекс клавиатурой
  searchQuery.value = newValue;
  // Принудительное обновление реактивности Vue
  nextTick(() => {
    if (searchQuery.value !== newValue) {
      searchQuery.value = newValue;
    }
  });
};

// Фильтрация опций по поисковому запросу
const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options;
  }
  
  const query = searchQuery.value.toLowerCase();
  return props.options.filter((option) => {
    return option.label && option.label.toLowerCase().includes(query);
  });
});

// Поиск опции по значению
const findOptionByValue = (value: any): Option | undefined => {
  return props.options.find(option => option.value === value);
};

// Получение текущего значения (с учетом отложенного обновления)
const currentValue = computed(() => {
  if (props.deferredUpdate) {
    // Если tempValue не инициализирован, используем modelValue
    if (tempValue.value === null) {
      return Array.isArray(modelValue.value) ? [...(modelValue.value || [])] : (modelValue.value || null);
    }
    return tempValue.value;
  }
  return modelValue.value || (props.multiple ? [] : null);
});

// Получение тегов для множественного выбора
const selectedTags = computed(() => {
  if (!props.multiple || !Array.isArray(currentValue.value)) return [];
  
  return currentValue.value.map(value => {
    const option = findOptionByValue(value);
    return {
      value,
      label: option ? option.label : value
    };
  }).filter(tag => tag.label);
});

// Получение текста выбранного элемента
const selectedLabel = computed(() => {
  if (!currentValue.value) return '';
  
  if (props.multiple && Array.isArray(currentValue.value)) {
    return ''; // Для множественного выбора используем теги
  }
  
  const option = findOptionByValue(currentValue.value);
  return option ? option.label : '';
});

// Проверка, выбран ли элемент
const isSelected = (value: any): boolean => {
  if (props.multiple && Array.isArray(currentValue.value)) {
    return currentValue.value.includes(value);
  }
  return currentValue.value === value;
};

// Удаление тега
const removeTag = (valueToRemove: any) => {
  if (props.multiple) {
    const targetValue = props.deferredUpdate ? tempValue.value : modelValue.value;
    if (Array.isArray(targetValue)) {
      const newValue = targetValue.filter(v => v !== valueToRemove);
      
      if (props.deferredUpdate) {
        tempValue.value = newValue;
      } else {
        modelValue.value = newValue;
        emit('update:modelValue', newValue);
      }
    }
  }
};

// Выбор опции
const selectOption = (option: Option) => {
  if (props.multiple) {
    const targetValue = props.deferredUpdate ? tempValue.value : modelValue.value;
    const currentArray = Array.isArray(targetValue) ? [...targetValue] : [];
    const index = currentArray.findIndex((v) => v === option.value);
    
    if (index > -1) {
      currentArray.splice(index, 1);
    } else {
      currentArray.push(option.value);
    }
    
    if (props.deferredUpdate) {
      tempValue.value = currentArray;
    } else {
      modelValue.value = currentArray;
      emit('update:modelValue', currentArray);
    }
  } else {
    if (props.deferredUpdate) {
      tempValue.value = option.value;
      applyChanges();
    } else {
      modelValue.value = option.value;
      emit('update:modelValue', option.value);
      closeDialog();
    }
  }
};
</script>

<style lang="scss" scoped>
.multi-select {
  width: 100%;
  border-color: unset !important;

  &.unset-border {
    :deep(.el-select__wrapper) {
      border-color: white !important;
    }
  }

  :deep(.el-select__wrapper) {
    color: theme("colors.txmain");
    font-size: 14px;
    height: auto;
    padding: 10px 14px;
    border-radius: 16px !important;
    background-color: theme("colors.bgmain");
    box-shadow: none;

    &.is-focused {
      border-color: theme("colors.primary") !important;
    }
  }

  :deep(.el-tag) {
    background-color: theme("colors.primary");
    font-size: 12px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    line-height: 130% !important;
    border-radius: 6px !important;

    span {
      color: white !important;
    }

    i {
      color: white !important;
    }
  }
}
</style>

<style lang="scss">
  // Стили для мобильной модалки
  .select-dialog {
    @media (max-width: 768px) {
      margin: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      position: fixed !important;
      bottom: 0 !important;
      top: auto !important;
      left: 0 !important;
      transform: none !important;
      border-radius: 24px 24px 0 0 !important;

      .el-dialog__header {
        display: none;
      }

      .el-dialog__body {
        padding: 16px !important;
        border-radius: 24px 24px 0 0 !important;
      }
    }
  }

  .multi-select-popper {
    border-radius: 12px !important;

    .el-popper__arrow {
      display: none;
    }

    .el-select-dropdown__wrap {
      max-height: 350px !important;
    }
  }
</style>
